
import * as THREE from "../../../libs/three.js/build/three.module.js";
import {ensureHierarchyLoaded} from "../../digitization/volume/RoiPointLoader.js";
import {boxVolumeWorldAABB} from "./PointCloudQuery.js";
import {profileSegments, insideProfile, boxTouchesProfile} from "./profileRegion.js";
import {colorForDeviationMm, originalRgbaForNode} from "../DeviationAnalysis/DeviationAnalysis.js";

import {t} from "../../digitization/i18n/index.js";
// LAS export of EVERY point in the octree inside a region - not just the
// nodes that happen to be resident because the camera looked there (which is
// what collectLoaded*/collectDeviationColoredPoints in PointCloudQuery.js /
// DeviationAnalysis.js see: only the level of detail streamed in for the
// current view, so a clip export from a zoomed-out view is a sparse
// sliver of the real data).
//
// Walks the octree's own geometry-node tree (`pointcloud.pcoGeometry.root`,
// independent of visibleNodes / the render point budget), skips every
// branch whose bounding box misses the region, force-loads each remaining
// node (hierarchy first, then points - see RoiPointLoader.js for the
// details of that gate) and extracts the matching points from each node
// right after it has loaded. Extracting immediately (rather than loading
// everything first and reading afterwards) means a node Potree's LRU later
// evicts cannot cost points, and memory only holds the result.
//
// Octree nodes in this format are disjoint (a node's own points are not a
// resample of its children's), so the union of all nodes' points is exactly
// the full-resolution data - no duplicates.

const CHUNK_POINTS = 500000;
const DEFAULT_CONCURRENCY = 32;
const RAISED_NODE_LOAD_LIMIT = 64;

// Batched range requests (see walkPointcloud): nodes whose bytes lie within
// RUN_GAP_BYTES of each other share one request, up to RUN_MAX_BYTES each.
const RUN_GAP_BYTES = 64 * 1024;
const RUN_MAX_BYTES = 16 * 1024 * 1024;
const RUN_CONCURRENCY = 6;

// Nested raise/restore of Potree.maxNodesLoading, same idea as
// RoiPointLoader.loadRoiPoints (its own module-level state is private, and
// both may run at once, so this keeps its own counter).
let raiseDepth = 0;
let valueBeforeAnyRaise = null;

function raiseNodeLoadLimit(){
	if(raiseDepth === 0){
		valueBeforeAnyRaise = Potree.maxNodesLoading;
	}
	raiseDepth++;
	Potree.maxNodesLoading = Math.max(Potree.maxNodesLoading, RAISED_NODE_LOAD_LIMIT);
}

function restoreNodeLoadLimit(){
	raiseDepth--;
	if(raiseDepth === 0){
		Potree.maxNodesLoading = valueBeforeAnyRaise;
	}
}

// Same node-local -> world transform as PointCloudQuery.nodeToWorldMatrix:
// positions are relative to the node's own boundingBox.min.
function nodeToWorldMatrix(pointcloud, node, localFrame = false){
	let nodeMatrix = new THREE.Matrix4().makeTranslation(...node.boundingBox.min.toArray());
	if(localFrame){
		return nodeMatrix; // the point cloud's own (octree) coordinates, without its world transform
	}
	return new THREE.Matrix4().multiplyMatrices(pointcloud.matrixWorld, nodeMatrix);
}

// Like RoiPointLoader.ensurePointsLoaded, but woken by the loader's own
// completion callback (OctreeLoader.js: node.onLoadedCallbacks) instead of
// a 20 ms poll per node; a slow poll stays as a safety net (a failed load
// never fires the callback and needs load() re-issued, and load() is a
// silent no-op while Potree.numNodesLoading >= maxNodesLoading).
const LOAD_TIMEOUT_MS = 60000;
const LOAD_SAFETY_POLL_MS = 250;

function loadNodePoints(node){
	if(node.loaded){
		return Promise.resolve();
	}

	return new Promise((resolve, reject) => {
		let start = Date.now();
		let timer = null;
		let settled = false;

		let done = () => {
			if(settled) return;
			settled = true;
			clearTimeout(timer);
			resolve();
		};

		let attempt = () => {
			if(settled) return;
			if(node.loaded){
				done();
				return;
			}
			if(Date.now() - start > LOAD_TIMEOUT_MS){
				settled = true;
				reject(new Error(t("Tidsudløb ved indlæsning af punktsky-node \"{0}\".", node.name)));
				return;
			}
			if(!node.loading){
				node.load();
			}
			timer = setTimeout(attempt, node.loading ? LOAD_SAFETY_POLL_MS : 20);
		};

		(node.onLoadedCallbacks = node.onLoadedCallbacks || []).push(done);
		attempt();
	});
}

// One node's decoded points as plain arrays: {position: Float32Array
// (node-local), rgba: Uint8Array|null}. Prefers the 2.0 loader's own
// loadDecoded() (OctreeLoader.js: no viewer state, LRU or load gate
// involved); a node the viewer already holds is read as is.
async function getNodePoints(node){
	let fromGeometry = () => {
		let attributes = node.geometry && node.geometry.attributes;
		if(!attributes || !attributes.position){
			return null;
		}
		return {position: attributes.position.array, rgba: originalRgbaForNode(node) || (attributes.rgba ? attributes.rgba.array : null)};
	};

	if(node.loaded){
		let held = fromGeometry();
		if(held){
			return held;
		}
	}

	let loader = node.octreeGeometry && node.octreeGeometry.loader;
	if(loader && loader.loadDecoded){
		return loader.loadDecoded(node);
	}

	await loadNodePoints(node);
	return fromGeometry();
}

class PointAccumulator{
	constructor(){
		this.positionChunks = [];
		this.colorChunks = [];
		this.count = 0;
		this.chunkFill = CHUNK_POINTS; // forces a first chunk
		this.positions = null;
		this.colors = null;
	}

	push(x, y, z, r, g, b){
		if(this.chunkFill >= CHUNK_POINTS){
			this.positions = new Float64Array(CHUNK_POINTS * 3);
			this.colors = new Float32Array(CHUNK_POINTS * 4);
			this.positionChunks.push(this.positions);
			this.colorChunks.push(this.colors);
			this.chunkFill = 0;
		}
		let i = this.chunkFill++;
		this.positions[i * 3 + 0] = x;
		this.positions[i * 3 + 1] = y;
		this.positions[i * 3 + 2] = z;
		this.colors[i * 4 + 0] = r;
		this.colors[i * 4 + 1] = g;
		this.colors[i * 4 + 2] = b;
		this.colors[i * 4 + 3] = 1;
		this.count++;
	}
}

// Core traversal, in two phases. Options:
//   nodeFilter(worldBox) -> bool   cheap test for whole octree branches
//   pointHandler(world, rgba, i, color) -> bool   per point; `rgba` is the
//       node's own 0-255 color array (or null), `color` is a 3-element
//       array to fill with 0-1 RGB for an accepted point
//   extractNode(position, rgba, matrix, accumulator, classification)   optional
//       fast path replacing the per-point pointHandler loop; `position` is the
//       node's Float32 xyz array, `matrix` is node-local -> world (or ->
//       the cloud's own local frame with localFrame), `classification` the raw
//       Uint8/Float32 classification values (null when the cloud has none)
//   maxLevel                       octree levels deeper than this are neither indexed nor loaded
//   planNodes(nodes) -> nodes      optional step between index and load: picks the subset to load (e.g. a level budget)
//   localFrame                     matrix -> the point cloud's own coordinates instead of world coordinates
//   accumulator                    PointAccumulator shared across clouds
//   onProgress({phase, nodesVisited, nodesQueued, points})
//       phase "index": walking the hierarchy (nodesQueued = nodes found so
//       far); phase "load": nodesQueued is the final total
//   shouldCancel() -> bool
//
// 1. INDEX: walk only the hierarchy (small chunk requests, no point data)
//    and collect every node whose box meets the region.
// 2. LOAD: octree nodes are small (often a few KB to a few tens of KB) and
//    the converter writes them next to each other in octree.bin, so
//    fetching them one request each is latency-bound (thousands of
//    round-trips). Instead the needed nodes are sorted by file offset,
//    neighbours are merged into runs, each run is fetched with ONE range
//    request, cut back into nodes, decoded on a bounded set of workers and
//    extracted. Nothing goes through the viewer (no node.loaded, load gate
//    or LRU - see OctreeLoader.loadDecoded).
export async function walkPointcloud(pointcloud, {nodeFilter, pointHandler, extractNode, accumulator, onProgress, shouldCancel, concurrency, maxLevel = Infinity, planNodes = null, localFrame = false}){
	let root = pointcloud.pcoGeometry && pointcloud.pcoGeometry.root;
	if(!root){
		return;
	}

	let tStart = performance.now();

	// ---- phase 1: index
	let queue = [root];
	let inFlight = 0;
	let failure = null;
	let nodes = [];
	let nodesSkipped = 0;

	let indexNode = async (node) => {
		if(node.level > maxLevel){
			return;
		}
		let worldBox = node.boundingBox.clone().applyMatrix4(localFrame ? new THREE.Matrix4() : pointcloud.matrixWorld);
		if(!nodeFilter(worldBox)){
			nodesSkipped++;
			return;
		}

		await ensureHierarchyLoaded(node);
		for(let child of Object.values(node.children || {})){
			if(child){
				queue.push(child);
			}
		}

		nodes.push(node);
		if(onProgress && nodes.length % 25 === 0){
			onProgress({phase: "index", nodesVisited: 0, nodesQueued: nodes.length, points: accumulator.count});
		}
	};

	let indexWorker = async () => {
		while(!failure){
			if(shouldCancel && shouldCancel()){
				failure = new Error(t("Annulleret"));
				return;
			}

			let node = queue.pop();
			if(!node){
				if(inFlight === 0){
					return;
				}
				await new Promise(resolve => setTimeout(resolve, 5));
				continue;
			}

			inFlight++;
			try{
				await indexNode(node);
			}catch(e){
				failure = e;
			}finally{
				inFlight--;
			}
		}
	};

	await Promise.all(Array.from({length: concurrency}, indexWorker));
	if(failure){
		throw failure;
	}

	if(planNodes){
		nodes = planNodes(nodes);
	}

	let tIndexed = performance.now();

	// ---- phase 2: load + extract
	let world = new THREE.Vector3();
	let color = [0, 0, 0];
	let nodesVisited = 0;
	let pointsDecoded = 0;
	let requests = 0;
	let bytesFetched = 0;

	let consume = (node, data) => {
		if(data && data.position.length > 0){
			let matrix = nodeToWorldMatrix(pointcloud, node, localFrame);
			let position = data.position;
			let n = position.length / 3;
			pointsDecoded += n;
			if(extractNode){
				extractNode(position, data.rgba, matrix, accumulator, data.classification || null);
			}else{
				for(let i = 0; i < n; i++){
					world.set(position[i * 3], position[i * 3 + 1], position[i * 3 + 2]).applyMatrix4(matrix);
					if(pointHandler(world, data.rgba, i, color)){
						accumulator.push(world.x, world.y, world.z, color[0], color[1], color[2]);
					}
				}
			}
		}

		nodesVisited++;
		if(onProgress){
			onProgress({phase: "load", nodesVisited, nodesQueued: nodes.length, points: accumulator.count});
		}
	};

	let loader = root.octreeGeometry && root.octreeGeometry.loader;
	let canBatch = !!(loader && loader.fetchRange && loader.decodeBuffer);

	let remote = [];
	for(let node of nodes){
		if(node.loaded && node.geometry && node.geometry.attributes.position){
			let a = node.geometry.attributes;
			consume(node, {position: a.position.array, rgba: originalRgbaForNode(node) || (a.rgba ? a.rgba.array : null), classification: a.classification ? a.classification.array : null});
		}else if(canBatch && node.byteSize === 0n){
			consume(node, null);
		}else{
			remote.push(node);
		}
	}

	if(canBatch){
		remote.sort((a, b) => Number(a.byteOffset) - Number(b.byteOffset));

		let runs = [];
		let current = null;
		for(let node of remote){
			let start = Number(node.byteOffset);
			let end = start + Number(node.byteSize);
			if(current && start - current.end <= RUN_GAP_BYTES && end - current.start <= RUN_MAX_BYTES){
				current.nodes.push(node);
				current.end = Math.max(current.end, end);
			}else{
				current = {start, end, nodes: [node]};
				runs.push(current);
			}
		}

		let nextRun = 0;
		let runWorker = async () => {
			while(!failure){
				if(shouldCancel && shouldCancel()){
					failure = new Error(t("Annulleret"));
					return;
				}

				let index = nextRun++;
				if(index >= runs.length){
					return;
				}

				let run = runs[index];
				try{
					let buffer = await loader.fetchRange(run.start, run.end - 1);
					requests++;
					bytesFetched += buffer.byteLength;
					await Promise.all(run.nodes.map(async (node) => {
						let from = Number(node.byteOffset) - run.start;
						let part = run.nodes.length === 1 ? buffer : buffer.slice(from, from + Number(node.byteSize));
						consume(node, await loader.decodeBuffer(node, part));
					}));
				}catch(e){
					failure = e;
				}
			}
		};

		await Promise.all(Array.from({length: Math.min(RUN_CONCURRENCY, runs.length)}, runWorker));
	}else{
		// Other octree formats: the viewer's own loader, one node at a time.
		raiseNodeLoadLimit();
		try{
			let next = 0;
			let legacyWorker = async () => {
				while(!failure){
					if(shouldCancel && shouldCancel()){
						failure = new Error(t("Annulleret"));
						return;
					}

					let index = next++;
					if(index >= remote.length){
						return;
					}

					try{
						consume(remote[index], await getNodePoints(remote[index]));
					}catch(e){
						failure = e;
					}
				}
			};
			await Promise.all(Array.from({length: concurrency}, legacyWorker));
		}finally{
			restoreNodeLoadLimit();
		}
	}

	let tDone = performance.now();
	console.log(`[LAS-eksport] ${pointcloud.name || "punktsky"}: ${nodes.length} noder i området (${nodesSkipped} udenfor sprunget over) fundet på ${((tIndexed - tStart) / 1000).toFixed(1)} s; ${pointsDecoded} punkter dekodet (${accumulator.count} i alt) på ${((tDone - tIndexed) / 1000).toFixed(1)} s via ${requests} forespørgsler (${(bytesFetched / 1048576).toFixed(1)} MB)`);

	if(failure){
		throw failure;
	}
}

function finish(accumulator){
	let count = accumulator.count;
	let positions = new Float64Array(count * 3);
	let colors = new Float32Array(count * 4);

	let offset = 0;
	for(let c = 0; c < accumulator.positionChunks.length; c++){
		let n = Math.min(CHUNK_POINTS, count - offset);
		positions.set(accumulator.positionChunks[c].subarray(0, n * 3), offset * 3);
		colors.set(accumulator.colorChunks[c].subarray(0, n * 4), offset * 4);
		offset += n;
	}

	return {positions, colors, count};
}

// Every point of every given point cloud inside a BoxVolume (Clip Volume /
// box section), with its stored color. Returns {positions: Float64Array
// (xyz), colors: Float32Array (rgba 0-1), count}.
export async function exportAllPointsInVolume(pointclouds, volume, {onProgress, shouldCancel, concurrency = DEFAULT_CONCURRENCY} = {}){
	let inverse = new THREE.Matrix4().copy(volume.matrixWorld).invert();
	let worldBox = boxVolumeWorldAABB(volume);
	let toLocal = new THREE.Matrix4();
	let accumulator = new PointAccumulator();

	for(let pointcloud of pointclouds){
		await walkPointcloud(pointcloud, {
			nodeFilter: (box) => box.intersectsBox(worldBox),
			// Hot loop: node-local -> volume-local with the combined matrix and
			// a plain typed-array walk, world coordinates only for the points
			// that are kept.
			extractNode: (position, rgba, nodeToWorld, acc) => {
				toLocal.multiplyMatrices(inverse, nodeToWorld);
				let m = toLocal.elements;
				let w = nodeToWorld.elements;
				let n = position.length / 3;
				for(let i = 0; i < n; i++){
					let px = position[i * 3], py = position[i * 3 + 1], pz = position[i * 3 + 2];
					let lx = m[0] * px + m[4] * py + m[8] * pz + m[12];
					if(lx > 0.5 || lx < -0.5) continue;
					let ly = m[1] * px + m[5] * py + m[9] * pz + m[13];
					if(ly > 0.5 || ly < -0.5) continue;
					let lz = m[2] * px + m[6] * py + m[10] * pz + m[14];
					if(lz > 0.5 || lz < -0.5) continue;
					let x = w[0] * px + w[4] * py + w[8] * pz + w[12];
					let y = w[1] * px + w[5] * py + w[9] * pz + w[13];
					let z = w[2] * px + w[6] * py + w[10] * pz + w[14];
					if(rgba){
						acc.push(x, y, z, rgba[i * 4] / 255, rgba[i * 4 + 1] / 255, rgba[i * 4 + 2] / 255);
					}else{
						acc.push(x, y, z, 1, 1, 1);
					}
				}
			},
			accumulator, onProgress, shouldCancel, concurrency,
		});
	}

	return finish(accumulator);
}

// Every point of every given point cloud inside a 2D profile: the profile's polyline (profile.points, x/y) widened to
// profile.width (half to each side - see profileRegion.js), at ALL heights, exactly the region the profile window shows. Full
// resolution from the octree, not just the nodes streamed in for the current view (same approach as exportAllPointsInVolume).
// Returns {positions: Float64Array (xyz), colors: Float32Array (rgba 0-1), count}.
export async function exportAllPointsInProfile(pointclouds, profile, {onProgress, shouldCancel, concurrency = DEFAULT_CONCURRENCY} = {}){
	let segments = profileSegments(profile.points, profile.width);
	let accumulator = new PointAccumulator();

	if(segments.length === 0){
		return finish(accumulator);
	}

	for(let pointcloud of pointclouds){
		await walkPointcloud(pointcloud, {
			nodeFilter: (box) => boxTouchesProfile(segments, box.min.x, box.min.y, box.max.x, box.max.y),
			extractNode: (position, rgba, nodeToWorld, acc) => {
				let w = nodeToWorld.elements;
				let n = position.length / 3;
				for(let i = 0; i < n; i++){
					let px = position[i * 3], py = position[i * 3 + 1], pz = position[i * 3 + 2];
					let x = w[0] * px + w[4] * py + w[8] * pz + w[12];
					let y = w[1] * px + w[5] * py + w[9] * pz + w[13];
					if(!insideProfile(segments, x, y)) continue;
					let z = w[2] * px + w[6] * py + w[10] * pz + w[14];
					if(rgba){
						acc.push(x, y, z, rgba[i * 4] / 255, rgba[i * 4 + 1] / 255, rgba[i * 4 + 2] / 255);
					}else{
						acc.push(x, y, z, 1, 1, 1);
					}
				}
			},
			accumulator, onProgress, shouldCancel, concurrency,
		});
	}

	return finish(accumulator);
}

// Every point of every given point cloud within maxDistanceM of a reference
// plane, colored with the deviation colormap (the same colors
// applyDeviationColoring paints - computed here rather than read from the
// nodes, so the result is identical whether or not a node was on screen).
export async function exportAllDeviationPoints(pointclouds, planePoint, planeNormal, maxDistanceM, {onProgress, shouldCancel, concurrency = DEFAULT_CONCURRENCY} = {}){
	let normal = planeNormal.clone().normalize();
	let diff = new THREE.Vector3();
	let center = new THREE.Vector3();
	let half = new THREE.Vector3();
	let rangeMm = maxDistanceM * 1000;
	let accumulator = new PointAccumulator();

	// A branch can contain points within the margin only if the plane's
	// distance to its box (center distance minus the box's extent along the
	// plane normal) is within the margin.
	let nodeFilter = (box) => {
		box.getCenter(center);
		box.getSize(half).multiplyScalar(0.5);
		let reach = half.x * Math.abs(normal.x) + half.y * Math.abs(normal.y) + half.z * Math.abs(normal.z);
		let distance = diff.subVectors(center, planePoint).dot(normal);
		return Math.abs(distance) <= maxDistanceM + reach;
	};

	for(let pointcloud of pointclouds){
		await walkPointcloud(pointcloud, {
			nodeFilter,
			pointHandler: (world, rgba, i, color) => {
				let distanceM = diff.subVectors(world, planePoint).dot(normal);
				if(Math.abs(distanceM) > maxDistanceM){
					return false;
				}
				let [r, g, b] = colorForDeviationMm(distanceM * 1000, rangeMm);
				color[0] = r / 255;
				color[1] = g / 255;
				color[2] = b / 255;
				return true;
			},
			accumulator, onProgress, shouldCancel, concurrency,
		});
	}

	return finish(accumulator);
}
